import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logSupabaseError, logError } from '@/utils/sentry';

export const useAcademyProgress = (userId: string | null) => {
  const [progress, setProgress] = useState<Record<number, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchProgress = useCallback(async () => {
    if (!userId) {
      setProgress({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('academy_progress')
        .select('module_id')
        .eq('user_id', userId);

      if (error) {
        logSupabaseError('Academy progress: fetch', error);
        setProgress({});
        return;
      }

      const mapped: Record<number, boolean> = {};
      for (const row of data ?? []) {
        mapped[row.module_id] = true;
      }
      setProgress(mapped);
    } catch (err) {
      logError('Academy progress: unexpected fetch error', err, { hook: 'useAcademyProgress' });
      setProgress({});
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const completeModule = useCallback(async (moduleId: number) => {
    if (!userId) return;
    // Optimistic update — UI responds instantly
    setProgress(prev => ({ ...prev, [moduleId]: true }));

    const { error } = await supabase
      .from('academy_progress')
      .upsert(
        { user_id: userId!, module_id: moduleId },
        { onConflict: 'user_id,module_id', ignoreDuplicates: true }
      );

    if (error) {
      logSupabaseError('Academy progress: upsert', error);
      // Optimistic state is kept — user sees completion, will be correct on next load
    }
  }, [userId]);

  return { progress, isLoading, completeModule };
};
